/* ───────── Remote kiosk config (sourced from Till attributes via GraphQL) ───────── */

// Galaga attributes stored on the Till, parsed into typed fields.
export interface KioskRemoteConfig {
  // Theme
  colorAccent?: string
  colorAccentHover?: string
  colorBg?: string
  colorSurface?: string
  colorSurface2?: string
  colorText?: string
  colorMuted?: string

  // Branding
  logoUrl?: string | null
  /** Kioskens visningsnamn (titel på startskärmen, manifest-name). */
  appName?: string
  pageTitle?: string
  /** PWA-konfig — påverkar "Lägg till på hemskärmen" på iOS/Android.
   *  Manifest:en byggs dynamiskt vid load baserat på dessa fält så varje
   *  kund kan ha egen ikon + namn på iPaden utan deploy. */
  appIconUrl192?: string | null
  appIconUrl512?: string | null
  /** Themecolor i manifest (status bar / splash background på iOS).
   *  Default = colorAccent. */
  pwaThemeColor?: string

  /** Bildspel/media-lista för startskärmen — används OM den har items.
   *  Annars faller vi tillbaka till videoUrl* (singel video).
   *  Itemtyp auto-detekteras från URL-extension om type=undefined. */
  startScreenMedia?: StartScreenMedia[]

  // Orientation
  layoutMode?: 'portrait' | 'landscape'

  // Layout
  categoryNavStyle?: string
  defaultProductLayout?: string
  categoryColumns?: number // 2 or 3 columns for category grid
  /** Hoppa över kategorigrid:en — kunden landar direkt på "HELA MENYN"-vyn
   *  efter Äta här/Ta med-valet. Användbart för restauranger med få kategorier
   *  (Kebabdudes etc.) där grid:en bara är extra klick. */
  skipCategoryGrid?: boolean
  /** Visa sticky kategoribar i HELA MENYN — klick på en kategori smooth-
   *  scrollar till sektionen, aktiv kategori highlightas medan kund scrollar.
   *  Bara meningsfullt när skipCategoryGrid är på (i vanligt läge har user
   *  redan kategorigrid:en som navigation). */
  fullMenuCategoryBar?: boolean
  /** Justering av kategoribarens innehåll. 'center' centrerar knapparna
   *  när de får plats; vid overflow scrollar de horisontellt från vänster.
   *  'left' (default) vänsterställer alltid. */
  fullMenuCategoryBarAlign?: 'left' | 'center'

  // Collapse
  collapseEnabled?: boolean
  maxCollapsedRows?: number

  // Timeouts
  inactivityTimeout?: number
  countdownDuration?: number
  receiptAutoDismiss?: number

  // Tips
  tipsEnabled?: boolean
  tipPercentages?: number[]

  // Language
  defaultLocale?: string
  availableLocales?: string[]
}

/** En slide i startskärmens bildspel. Lagras som JSON-array under
 *  `galaga.branding.startScreenMedia` på Till. */
export interface StartScreenMedia {
  url: string
  type: 'image' | 'video'
  /** Visningstid i sekunder (image-slides). Default 5s. För video används
   *  videons egen längd om durationSec är 0; annars klipps videon. */
  durationSec: number
}

// Attributes arrive as proper JSON values; reject values of the wrong type.

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined
}

function bool(v: unknown): boolean | undefined {
  return typeof v === 'boolean' ? v : undefined
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function arr<T>(v: unknown): T[] | undefined {
  return Array.isArray(v) ? (v as T[]) : undefined
}

/** Parse typed Galaga settings from the GraphQL Till response. */
export function parseTillAttributes(till: Record<string, unknown>): KioskRemoteConfig {
  return {
    // Theme
    colorAccent: str(till.colorAccent),
    colorAccentHover: str(till.colorAccentHover),
    colorBg: str(till.colorBg),
    colorSurface: str(till.colorSurface),
    colorSurface2: str(till.colorSurface2),
    colorText: str(till.colorText),
    colorMuted: str(till.colorMuted),

    // Branding
    logoUrl: str(till.logoUrl) ?? null,
    appName: str(till.appName),
    pageTitle: str(till.pageTitle),
    appIconUrl192: str(till.appIconUrl192) ?? null,
    appIconUrl512: str(till.appIconUrl512) ?? null,
    pwaThemeColor: str(till.pwaThemeColor),
    startScreenMedia: arr<StartScreenMedia>(till.startScreenMedia) ?? [],

    // Orientation
    layoutMode: till.layoutMode === 'landscape' ? 'landscape' : 'portrait',

    // Layout
    categoryNavStyle: str(till.categoryNavStyle),
    defaultProductLayout: str(till.defaultProductLayout),
    categoryColumns: num(till.categoryColumns),
    skipCategoryGrid: bool(till.skipCategoryGrid),
    fullMenuCategoryBar: bool(till.fullMenuCategoryBar),
    fullMenuCategoryBarAlign: till.fullMenuCategoryBarAlign === 'center' ? 'center' : 'left',

    // Collapse
    collapseEnabled: bool(till.collapseEnabled),
    maxCollapsedRows: num(till.maxCollapsedRows),

    // Timeouts
    inactivityTimeout: num(till.inactivityTimeout),
    countdownDuration: num(till.countdownDuration),
    receiptAutoDismiss: num(till.receiptAutoDismiss),

    // Tips
    tipsEnabled: bool(till.tipsEnabled),
    tipPercentages: arr<number>(till.tipPercentages),

    // Language
    defaultLocale: str(till.defaultLocale),
    availableLocales: arr<string>(till.availableLocales),
  }
}
